<template>
  <CollapseView
    ref="collapse"
    :open="open && !disabled"
    @open="$emit('open')"
    @close="$emit('close')"
    :data-e2e="dataE2e"
    :animation="animation"
    class="m-b-10"
    :class="{
      'is-disabled': disabled,
      panel: isPanel,
      'has-background-secondary': hasBackground,
      'is-shadowless': isShadowless,
      'has-border-light': !isBorderless,
      'expansion-disabled': isExpansionDisabled,
      [borderRadiusClass]: true
    }"
  >
    <template #trigger="triggerProps">
      <div
        class="trigger is-flex is-align-items-center"
        :class="{
          open: triggerProps.open,
          'is-borderless': hasBorderlessHeader,
          'is-flex-direction-row-reverse': iconPosition === Position.RIGHT,
          'is-flex-direction-row is-justify-content-start': iconPosition === Position.LEFT
        }"
        :data-e2e="AppCypressAttribute.buttons.COLLAPSE_TRIGGER"
      >
        <slot name="pre-icon-header" />
        <div
          v-if="showIcon"
          class="icon"
          :class="{
            animate: animate,
            rotate: triggerProps.open
          }"
          :data-e2e="AppCypressAttribute.buttons.COLLAPSE_TRIGGER_ICON"
        >
          <IconView pack="cls" size="is-small" custom-class="cls-caret-right" />
        </div>
        <slot name="header" :trigger-props="triggerProps" />
      </div>
      <slot name="preview" />
    </template>
    <div class="p-2">
      <slot />
    </div>
  </CollapseView>
</template>

<script>
import { CollapseEvent } from '@/modules/core/app/constants/CollapseEvent';
import { AppCypressAttribute } from '/@/modules/core/app/constants/AppCypressAttribute';
import IconView from '@/modules/core/ui/components/wrappers/IconView.vue';
import CollapseView from '@/modules/core/ui/components/wrappers/CollapseView.vue';
import { AnimationType } from '@/modules/core/app/enums/AnimationType';
import { Position } from '@/modules/core/app/constants/Position';
export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'Collapse',
  emits: ['open', 'close'],
  slotsDefinition: {
    preIconHeader: {},
    header: { triggerProps: Object },
    preview: {}
  },
  components: { IconView, CollapseView },
  props: {
    open: {
      type: Boolean,
      default: true
    },
    animate: {
      type: Boolean,
      default: true
    },
    ariaId: {
      type: String
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    iconPosition: {
      type: String,
      default: Position.LEFT,
      validator: (value) => [Position.LEFT, Position.RIGHT].includes(value)
    },
    isPanel: {
      type: Boolean,
      default: false
    },
    isBorderless: {
      type: Boolean,
      default: false
    },
    hasBorderlessHeader: {
      type: Boolean,
      default: true
    },
    isShadowless: {
      type: Boolean,
      default: false
    },
    hasBackground: {
      type: Boolean,
      default: false
    },
    dataE2e: {
      type: String,
      default: null
    },
    isExpansionDisabled: {
      type: Boolean,
      default: false
    },
    collapseId: {
      type: String,
      default: null
    },
    animation: {
      type: String,
      default: AnimationType.FADE
    },
    borderRadiusSize: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large'].includes(value)
    }
  },
  static: {
    AppCypressAttribute,
    Position
  },
  events() {
    return {
      [CollapseEvent.COLLAPSE]: this.onCollapse,
      [CollapseEvent.EXPAND]: this.onExpand
    };
  },
  computed: {
    isOpen() {
      return this.$refs.collapse.isOpen;
    },
    borderRadiusClass() {
      return `is-radius-${this.borderRadiusSize}`;
    }
  },
  methods: {
    toggle() {
      this.$refs.collapse.toggle();
    },
    onCollapse(collapseId) {
      if (collapseId !== this.collapseId || !this.isOpen) {
        return;
      }

      this.toggle();
    },
    onExpand(collapseId) {
      if (collapseId !== this.collapseId || this.isOpen) {
        return;
      }

      this.toggle();
    }
  }
};
</script>

<style scoped>
div.trigger {
  padding: var(--ce-12px) var(--ce-15px);

  &.is-borderless {
    border-bottom: unset !important;
  }

  &.open {
    border-bottom: var(--ce-1px) solid var(--ce-grey-lightest);
  }

  .icon {
    color: var(--ce-primary-dark-20);
    font-size: var(--ce-14px);

    &.animate {
      transition: transform 0.3s ease;
      transform-origin: center;
    }

    &.rotate {
      transform: rotate(90deg);
    }
  }
}

.expansion-disabled :deep(.collapse-content) {
  display: none !important;
}
</style>
